/**
 * Recharge Subscription Widget Customization
 * Applies custom styling to the Recharge subscription widget's shadow DOM (parity with Derma-UK Live theme).
 */

/** @returns {{ recharge_frequency_title?: string; subscribe_button_text?: string; add_to_cart_text?: string; preorder_text?: string }} */
function getThemeStrings() {
  if (typeof Theme === 'undefined') {
    return {};
  }
  return {
    recharge_frequency_title: Theme.recharge_frequency_title,
    subscribe_button_text: Theme.subscribe_button_text,
    add_to_cart_text: Theme.add_to_cart_text,
    preorder_text: Theme.preorder_text,
  };
}

/** Main PDP submit (not sticky — that appears first in DOM and would steal querySelector). */
function getMainAddToCartButton() {
  return (
    document.querySelector('#MainContent product-form-component button[name="add"]') ||
    document.querySelector('product-form-component button[name="add"]')
  );
}

function getStickyAddToCartButton() {
  return document.querySelector('sticky-add-to-cart button.sticky-add-to-cart__button');
}

/**
 * Horizon 3.5 main button: `.add-to-cart-text__content > span > span` holds the phrase.
 * Sticky bar: first child span holds the phrase; a sibling span holds quantity — do not replace the wrapper's textContent.
 * Live / flat markup: text lives directly on `.add-to-cart-text__content`.
 * @param {Element | null} button
 * @returns {HTMLElement | null}
 */
function getAddToCartLabelTextTarget(button) {
  if (!(button instanceof HTMLElement)) return null;
  const wrapper = button.querySelector('.add-to-cart-text__content');
  if (!wrapper) return null;
  const mid = wrapper.firstElementChild;
  if (!mid) return wrapper instanceof HTMLElement ? wrapper : null;
  const deep = mid.firstElementChild;
  if (deep instanceof HTMLElement) return deep;
  return mid instanceof HTMLElement ? mid : null;
}

/** @param {string} nextText */
function setPdpAddToCartLabelText(nextText) {
  const mainBtn = getMainAddToCartButton();
  const stickyBtn = getStickyAddToCartButton();
  for (const btn of [mainBtn, stickyBtn]) {
    const target = getAddToCartLabelTextTarget(btn);
    if (target) target.textContent = nextText;
  }
}

class RechargeWidgetCustomizer {
  constructor() {
    this.widgetSelector = 'recharge-subscription-widget';
    this.observer = null;
    this.styleId = 'recharge-custom-styles';
    this.benefitsStyleId = 'recharge-benefits-alignment-styles';
    this.pollingInterval = null;
    this.maxPollingAttempts = 50;
    this.pollingAttempts = 0;
    this.init();
  }

  init() {
    this.startWidgetPolling();
    this.observeWidgetChanges();
    this.setupEventListeners();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.applyCustomStyles();
      });
    } else {
      this.applyCustomStyles();
    }
  }

  setupEventListeners() {
    document.addEventListener('recharge:widget:loaded', () => {
      this.applyCustomStyles();
    });

    document.addEventListener('variant:update', () => {
      setTimeout(() => {
        this.restartPolling();
        this.applyCustomStyles();
      }, 150);
    });

    document.addEventListener('variant:changed', () => {
      setTimeout(() => this.applyCustomStyles(), 100);
    });
  }

  startWidgetPolling() {
    if (this.pollingInterval) {
      return;
    }

    this.pollingInterval = setInterval(() => {
      const widget = document.querySelector(this.widgetSelector);

      if (widget?.shadowRoot) {
        this.applyCustomStyles();
        this.stopWidgetPolling();
      } else if (this.pollingAttempts >= this.maxPollingAttempts) {
        this.stopWidgetPolling();
      }

      this.pollingAttempts++;
    }, 200);
  }

  restartPolling() {
    this.stopWidgetPolling();
    this.startWidgetPolling();
  }

  stopWidgetPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
      this.pollingAttempts = 0;
    }
  }

  applyCustomStyles() {
    const widget = document.querySelector(this.widgetSelector);

    if (!widget) {
      return;
    }

    this.addWidgetTitle(widget);

    if (widget.shadowRoot) {
      this.handleShadowRootStyling(widget.shadowRoot);
    } else {
      this.waitForShadowRoot(widget);
    }
  }

  waitForShadowRoot(widget) {
    let attempts = 0;
    const maxAttempts = 20;

    const checkShadowRoot = () => {
      if (widget.shadowRoot) {
        this.handleShadowRootStyling(widget.shadowRoot);
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(checkShadowRoot, 200);
      }
    };

    setTimeout(checkShadowRoot, 200);
  }

  handleShadowRootStyling(shadowRoot) {
    if (!shadowRoot.querySelector(`#${this.styleId}`)) {
      this.injectStyles(shadowRoot);
    } else {
      this.setupButtonTextUpdates(shadowRoot);
    }

    this.handleBenefitsStyling(shadowRoot);
  }

  addWidgetTitle(widget) {
    if (!widget || widget.previousElementSibling?.classList.contains('h5')) {
      return;
    }

    const title = document.createElement('h3');
    title.classList.add('h5');
    const themeStrings = getThemeStrings();
    title.textContent =
      themeStrings.recharge_frequency_title || 'Choose your frequency:';

    if (widget.parentNode) {
      widget.parentNode.insertBefore(title, widget);
    }
  }

  setupButtonTextUpdates(shadowRoot) {
    const radios = shadowRoot.querySelectorAll('.rc-purchase-option__input');
    if (!radios.length) {
      return;
    }

    const subscriptionRadio =
      shadowRoot.querySelector('.rc-purchase-option__input[value="subscription"]') ||
      (radios.length > 1 ? radios[radios.length - 1] : null);

    if (!subscriptionRadio) {
      return;
    }

    this.updateButtonText(shadowRoot);

    radios.forEach((radio) => {
      if (radio.dataset.themeRechargeTextBound === 'true') {
        return;
      }
      radio.dataset.themeRechargeTextBound = 'true';
      radio.addEventListener('change', () => {
        this.updateButtonText(shadowRoot);
        this.handleBenefitsStyling(shadowRoot);
      });
    });
  }

  updateButtonText(shadowRoot) {
    const radios = shadowRoot.querySelectorAll('.rc-purchase-option__input');
    const subscriptionRadio =
      shadowRoot.querySelector('.rc-purchase-option__input[value="subscription"]') ||
      (radios.length > 1 ? radios[radios.length - 1] : null);

    if (!subscriptionRadio) {
      return;
    }

    const mainSubmit = getMainAddToCartButton();
    const labelTarget = getAddToCartLabelTextTarget(mainSubmit);
    if (!labelTarget) {
      return;
    }

    const productForm = document.querySelector('product-form-component');
    const isPreorderTemplate = productForm && productForm.getAttribute('data-is-preorder') === 'true';

    const themeStrings = getThemeStrings();

    if (isPreorderTemplate) {
      const preorderText = themeStrings.preorder_text || 'Preorder';
      setPdpAddToCartLabelText(preorderText);
      return;
    }

    const subscribeText = themeStrings.subscribe_button_text || 'Subscribe';
    const addToCartText = themeStrings.add_to_cart_text || 'Add to cart';

    let nextText = subscriptionRadio.checked ? subscribeText : addToCartText;

    if (mainSubmit && mainSubmit.hasAttribute('data-button-label')) {
      const customLabel = mainSubmit.getAttribute('data-button-label');
      if (customLabel) {
        nextText = customLabel;
      }
    }

    setPdpAddToCartLabelText(nextText);
  }

  injectStyles(shadowRoot) {
    if (shadowRoot.querySelector(`#${this.styleId}`)) {
      return;
    }

    const style = document.createElement('style');
    style.id = this.styleId;
    style.textContent = this.getCustomCSS();
    shadowRoot.appendChild(style);

    this.setupButtonTextUpdates(shadowRoot);
  }

  handleBenefitsStyling(widgetShadowRoot) {
    const rcBenefits = widgetShadowRoot.querySelector('rc-benefits');

    if (!rcBenefits) {
      return;
    }

    if (rcBenefits.shadowRoot) {
      this.injectBenefitsStyles(rcBenefits.shadowRoot);
      return;
    }

    let attempts = 0;
    const maxAttempts = 20;

    const waitForBenefitsShadowRoot = () => {
      if (rcBenefits.shadowRoot) {
        this.injectBenefitsStyles(rcBenefits.shadowRoot);
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(waitForBenefitsShadowRoot, 100);
      }
    };

    setTimeout(waitForBenefitsShadowRoot, 100);
  }

  injectBenefitsStyles(benefitsShadowRoot) {
    if (benefitsShadowRoot.querySelector(`#${this.benefitsStyleId}`)) {
      return;
    }

    const style = document.createElement('style');
    style.id = this.benefitsStyleId;
    style.textContent = this.getBenefitsAlignmentCSS();
    benefitsShadowRoot.appendChild(style);
  }

  getBenefitsAlignmentCSS() {
    return `
      .compact.rc-benefits__list li {
        display: flex;
        align-items: flex-start;
        padding-left: 0;
      }

      .compact.rc-benefits__list li::before {
        position: static;
        top: auto;
        left: auto;
        flex-shrink: 0;
        margin-right: 5px;
        margin-top: 1px;
      }
    `;
  }

  getCustomCSS() {
    return `
      .rc-purchase-option {
        margin-bottom: 0 !important;
        border-color: #d0d5dd !important;
      }

      .rc-purchase-option__label {
        padding: 12px 20px !important;
      }

      .rc-purchase-option__selector {
        font-size: 14px !important;
        font-weight: 300 !important;
        line-height: 18px !important;
      }

      .rc-purchase-option[rc-selected] .rc-purchase-option__selector {
        font-weight: 700 !important;
      }

      .rc-purchase-option[rc-selected] {
        border-color: #5b6670 !important;
      }

      .rc-selection__onetime {
        border-radius: 3px 3px 0 0 !important;
      }

      .rc-purchase-option__subscription {
        border-radius: 0 0 3px 3px !important;
        margin-top: -1px !important;
      }

      .rc-purchase-option__subscription[rc-not-selected] {
        border-top: 1px solid #5b6670 !important;
      }

      .rc-purchase-option__sub-container {
        flex-direction: column-reverse !important;
      }
    `;
  }

  observeWidgetChanges() {
    this.observer = new MutationObserver((mutations) => {
      let foundWidget = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = /** @type {Element} */ (node);
            const widget = element.matches?.(this.widgetSelector)
              ? element
              : element.querySelector?.(this.widgetSelector);

            if (widget) {
              foundWidget = true;
              this.addWidgetTitle(widget);

              if (widget.shadowRoot) {
                this.handleShadowRootStyling(widget.shadowRoot);
              } else {
                this.waitForShadowRoot(widget);
              }
            }
          }
        });

        if (mutation.type === 'childList' && mutation.target instanceof Element) {
          const widgets = mutation.target.querySelectorAll(this.widgetSelector);
          widgets.forEach((widget) => {
            if (widget && !foundWidget) {
              foundWidget = true;
              this.addWidgetTitle(widget);

              if (widget.shadowRoot) {
                this.handleShadowRootStyling(widget.shadowRoot);
              } else {
                this.waitForShadowRoot(widget);
              }
            }
          });
        }
      });
    });

    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false,
      attributeOldValue: false,
    });
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    this.stopWidgetPolling();
  }
}

function initializeRechargeCustomizer() {
  if (!window.rechargeWidgetCustomizer) {
    window.rechargeWidgetCustomizer = new RechargeWidgetCustomizer();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeRechargeCustomizer);
} else {
  initializeRechargeCustomizer();
}

window.addEventListener('load', initializeRechargeCustomizer);
setTimeout(initializeRechargeCustomizer, 1000);

function initializePreorderText() {
  const productForm = document.querySelector('product-form-component');
  const isPreorderTemplate = productForm && productForm.getAttribute('data-is-preorder') === 'true';

  if (isPreorderTemplate) {
    const themeStrings = getThemeStrings();
    const preorderText = themeStrings.preorder_text || 'Preorder';
    setPdpAddToCartLabelText(preorderText);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializePreorderText);
} else {
  initializePreorderText();
}

window.addEventListener('load', initializePreorderText);
setTimeout(initializePreorderText, 500);
